import { Injectable } from "@angular/core";
import { SearchInputType, TypeDetectionResult } from "../../../model";
import { GeoService } from "../../../../viewer-components/services/internal-services/geo.service";

/**
 * Unified service for detecting search input types
 * Replaces distributed detection logic across multiple services
 */
@Injectable({
  providedIn: "root",
})
export class SearchInputDetector {
  /**
   * Detects the input type with confidence and parsed data
   * @param input User input text
   * @returns Detection result with type, confidence, and parsed data
   */
  detectInput(input: string): TypeDetectionResult {
    if (!input?.trim()) {
      return {
        inputType: SearchInputType.TEXT,
        confidence: 0,
        originalInput: input,
      };
    }

    const trimmedInput = input.trim();

    // 1. Check for coordinates (highest priority for exact formats)
    const coordinateResult = this.detectCoordinates(trimmedInput);
    if (coordinateResult.confidence > 0.8) {
      return coordinateResult;
    }

    // 2. Check for WKT geometries
    const geometryResult = this.detectWKTGeometry(trimmedInput);
    if (geometryResult.confidence > 0.8) {
      return geometryResult;
    }

    // 3. Fallback to plain text search
    return {
      inputType: SearchInputType.TEXT,
      confidence: 0.5,
      originalInput: input,
    };
  }

  /**
   * Detects coordinate patterns with various formats
   * @param input Trimmed input text
   * @returns Detection result for coordinates
   */
  private detectCoordinates(input: string): TypeDetectionResult {
    // Common coordinate patterns:
    // - Decimal degrees: 40.7128, -74.0060
    // - With spaces: 40.7128 -74.0060
    // - Different order: -74.0060, 40.7128
    // - With parentheses: (40.7128, -74.0060)

    const patterns = [
      // Standard lat,lng or lng,lat with comma
      /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
      // Space separated
      /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)\s+[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
      // With parentheses
      /^\(\s*[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)\s*\)$/,
    ];

    for (const pattern of patterns) {
      if (pattern.test(input)) {
        const coordinates = this.parseCoordinates(input);
        if (coordinates) {
          return {
            inputType: SearchInputType.COORDINATE,
            confidence: 0.95,
            originalInput: input,
            parsedData: {
              coordinates,
            },
          };
        }
      }
    }

    return {
      inputType: SearchInputType.TEXT,
      confidence: 0,
      originalInput: input,
    };
  }

  /**
   * Detects WKT geometry patterns
   * @param input Trimmed input text
   * @returns Detection result for WKT geometries
   */
  private detectWKTGeometry(input: string): TypeDetectionResult {
    const wktPattern = /^(POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON|GEOMETRYCOLLECTION)\s*\(/i;

    if (wktPattern.test(input)) {
      const boundingBox = this.extractBoundingBox(input);

      return {
        inputType: SearchInputType.WKT_GEOMETRY,
        confidence: 0.95,
        originalInput: input,
        parsedData: {
          wktGeometry: input,
          boundingBox,
        },
      };
    }

    return {
      inputType: SearchInputType.TEXT,
      confidence: 0,
      originalInput: input,
    };
  }

  /**
   * Parse coordinates from various input formats
   * @param input Input string containing coordinates
   * @returns Parsed coordinates as [lat, lng] or null
   */
  private parseCoordinates(input: string): [number, number] | null {
    // Remove parentheses and extra spaces
    const cleaned = input.replace(/[()]/g, "").trim();

    // Split by comma or space
    const parts = cleaned.split(/[,\s]+/).map((p) => parseFloat(p.trim()));

    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      const [first, second] = parts;

      // Determine if it's lat,lng or lng,lat based on ranges
      // Latitude: -90 to 90, Longitude: -180 to 180
      if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
        // First could be latitude
        return [first, second];
      } else if (Math.abs(second) <= 90 && Math.abs(first) <= 180) {
        // Second is probably latitude (lng,lat format)
        return [second, first];
      } else {
        // Use as provided if both are in valid ranges
        return [first, second];
      }
    }

    return null;
  }

  /**
   * Extract bounding box from WKT geometry
   * @param wkt WKT geometry string
   * @returns Bounding box as [minLng, minLat, maxLng, maxLat] or undefined
   */
  private extractBoundingBox(wkt: string): [number, number, number, number] | undefined {
    return GeoService.wktPolygonToBbox(wkt, false);
  }
}
